// Thai / English UI. Thai is the default; the choice is remembered per browser.
// Strings are written in English in the code and looked up here; anything missing falls back to English.
export type Lang = 'th' | 'en'

const KEY = 'queza_support_lang'

function readLang(): Lang {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'en' ? 'en' : 'th'
  } catch {
    return 'th'
  }
}

export const lang: Lang = readLang()

export function setLang(next: Lang) {
  try {
    localStorage.setItem(KEY, next)
  } catch {
    /* ignore */
  }
  // Labels are resolved when modules load (menus, option lists) — a reload applies the switch everywhere.
  window.location.reload()
}

const TH: Record<string, string> = {
  "Mon–Fri": "จันทร์–ศุกร์",
  "every Monday": "ทุกวันจันทร์",
  "weekday": "วัน",
  "every day at": "ทุกวันเวลา",
  "every hour": "ทุกชั่วโมง",
  "every hour at minute": "ทุกชั่วโมง นาทีที่",
  "every": "ทุก",
  "minutes": "นาที",
  "hours": "ชั่วโมง",
  "Test run started — results appear below when it finishes.": "เริ่มรันเทสแล้ว — ผลจะขึ้นด้านล่างเมื่อเสร็จ",
  "Could not start the test run.": "เริ่มรันเทสไม่ได้",
  "System Tests": "ทดสอบระบบ",
  "Automated browser tests (queza-tester) run on the server against the live websites.": "เทสอัตโนมัติด้วยเบราว์เซอร์ (queza-tester) รันบน server กับเว็บจริง",
  "Run smoke tests": "รันเทสด่วน (smoke)",
  "Run all tests": "รันเทสทั้งหมด",
  "The tester service is not reachable.": "ติดต่อบริการ tester ไม่ได้",
  "It runs as the support-tester container; autodeploy starts it once its image is built.": "บริการนี้รันเป็น container support-tester — autodeploy จะเปิดให้เองหลัง build image เสร็จ",
  "Running": "กำลังรัน",
  "started": "เริ่ม",
  "scheduled": "รอบอัตโนมัติ",
  "Starting browser…": "กำลังเปิดเบราว์เซอร์…",
  "Last run had failures": "รอบล่าสุดมีเทสไม่ผ่าน",
  "Last run passed": "รอบล่าสุดผ่านทั้งหมด",
  "No runs yet": "ยังไม่เคยรัน",
  "Queued": "รอคิว",
  "Last error": "error ล่าสุด",
  "Automatic rounds": "รอบอัตโนมัติ",
  "Round": "รอบ",
  "Result": "ผล",
  "Failed tests": "เทสที่ไม่ผ่าน",
  "Time": "เวลา",
  "No test runs yet.": "ยังไม่มีผลการทดสอบ",
  "manual": "สั่งเอง",
  "Could not load tests.": "โหลดรายการเทสไม่ได้",
  "Selected": "เลือก",
  "Run selected": "รันที่เลือก",
  "Clear": "ล้าง",
  "Includes a destructive test (changes real data on the test shop).": "มีเทสที่เปลี่ยนข้อมูลจริง (ของร้านทดสอบ)",
  "steps": "ขั้นตอน",
  "needs": "ต้องมี",
  "View script": "ดูสคริปต์",
  "Test scripts are Markdown files in queza-support/tester/tests (ask Claude to write new ones — see AGENTS.md). They go live with the next deploy.": "สคริปต์เทสเป็นไฟล์ Markdown ใน queza-support/tester/tests (ให้ Claude เขียนเพิ่มได้ — ดู AGENTS.md) มีผลหลัง deploy ครั้งถัดไป",
  "Schedule saved.": "บันทึกรอบแล้ว",
  "Could not save the schedule.": "บันทึกรอบไม่สำเร็จ",
  "Showing the built-in default schedule. Saving here overrides it.": "กำลังแสดงรอบตั้งต้นของระบบ — กดบันทึกเพื่อใช้ค่าจากหน้านี้แทน",
  "Round name": "ชื่อรอบ",
  "When": "เวลา",
  "Custom (cron)": "กำหนดเอง (cron)",
  "Test files / folders": "ไฟล์ / โฟลเดอร์เทส",
  "(all)": "(ทั้งหมด)",
  "Include destructive tests": "รวมเทสที่เปลี่ยนข้อมูลจริง",
  "Add round": "เพิ่มรอบ",
  "Save schedule": "บันทึกรอบ",
  "Only Super Admin can change the schedule.": "เฉพาะ Super Admin แก้รอบได้",
  "Tester settings saved — used from the next run.": "บันทึกแล้ว — ใช้ตั้งแต่รอบถัดไป",
  "Could not save settings.": "บันทึกไม่สำเร็จ",
  "saved — leave empty to keep": "บันทึกไว้แล้ว — เว้นว่างเพื่อใช้ค่าเดิม",
  "Tester sign-in account": "บัญชีที่ tester ใช้ล็อกอิน",
  "Create a separate Support admin just for the tester (role: support). If it uses 2FA, paste the code shown under the QR (\"can’t scan?\") as the TOTP secret.": "สร้างบัญชี Support แยกไว้ให้ tester (role: support) ถ้าบัญชีเปิด 2FA ให้เอารหัสใต้ QR (\"สแกนไม่ได้? กรอกรหัสนี้\") มาใส่ช่อง TOTP secret",
  "TOTP secret (2FA)": "TOTP secret (2FA)",
  "Test shop ID (TEST_SHOP_ID)": "ID ร้านทดสอบ (TEST_SHOP_ID)",
  "Webhook (Discord / Slack / Google Chat)": "Webhook (Discord / Slack / Google Chat)",
  "Notify when": "แจ้งเมื่อ",
  "Only when a test fails": "เฉพาะตอนมีเทสไม่ผ่าน",
  "Every run": "ทุกรอบ",
  "Extra variables for test scripts": "ตัวแปรเพิ่มเติมสำหรับสคริปต์เทส",
  "Used as {{NAME}} in test files (for example TEST_OWNER_EMAIL).": "ใช้เป็น {{NAME}} ในไฟล์เทส (เช่น TEST_OWNER_EMAIL)",
  "Add variable": "เพิ่มตัวแปร",
  "Remove TOTP secret": "ลบ TOTP secret",
  "Websites under test": "เว็บที่ทดสอบ",
  "Set by the server (derived from the site hostnames).": "ตั้งโดย server (จากโดเมนของระบบ)",
  "Screenshot not available": "ไม่มีภาพหน้าจอ",
  "attempts": "ครั้งที่ลอง",
  "Console / network warnings": "คำเตือนจาก console / network",
  "Back to System Tests": "กลับไปหน้าทดสอบระบบ",
  "Could not load this run.": "โหลดผลรอบนี้ไม่ได้",
  "Test runs": "ผลการทดสอบ",
  "Results": "ผลการทดสอบ",
  "Schedule": "ตั้งรอบ",
  "Tests": "รายการเทส",
  "Settings": "ตั้งค่า",
  "Every hour 08:05–22:05": "ทุกชั่วโมง 08:05–22:05",
  "Every 30 minutes": "ทุก 30 นาที",
  "Every 3 hours": "ทุก 3 ชั่วโมง",
  "Every day 02:30": "ทุกวัน 02:30",
  "Weekdays 09:00": "จันทร์–ศุกร์ 09:00",
  "Every Monday 03:00": "ทุกวันจันทร์ 03:00",
  "Verified": "ตรวจแล้ว",
  "Past due": "ค้างชำระ",
  "Healthy": "ปกติ",
  "Degraded": "มีปัญหาบางส่วน",
  "Down": "ล่ม",
  "In progress": "กำลังดำเนินการ",
  "Renewal within 14 days": "ใกล้ต่ออายุใน 14 วัน",
  "No activity 30+ days": "ไม่มีความเคลื่อนไหว 30 วัน+",
  "Newest": "ใหม่ล่าสุด",
  "Renewal date": "วันต่ออายุ",
  "Last activity": "ใช้งานล่าสุด",
  "Bookings (30 days)": "การจอง (30 วัน)",
  "Export failed.": "ส่งออกไม่สำเร็จ",
  "Renewal": "ต่ออายุ",
  "days": "วัน",
  "in": "อีก",
  "Activity": "การใช้งาน",
  "bookings / 30d": "จอง / 30 วัน",
  "Last active": "ใช้งานล่าสุด",
  "Setup": "ตั้งค่าร้าน",
  "LINE OA connected": "เชื่อม LINE OA แล้ว",
  "LINE OA not connected": "ยังไม่เชื่อม LINE OA",
  "Onboarding done": "ตั้งค่าเริ่มต้นเสร็จแล้ว",
  "Onboarding not finished": "ยังตั้งค่าเริ่มต้นไม่เสร็จ",
  "Exporting…": "กำลังส่งออก…",
  "Watch list": "ต้องติดตาม",
  "Sort": "เรียงตาม",
  "Plan changed.": "เปลี่ยนแพ็กเกจแล้ว",
  "Action failed.": "ทำรายการไม่สำเร็จ",
  "Admin settings saved.": "บันทึกการตั้งค่าแล้ว",
  "Sign-in": "วิธีเข้าสู่ระบบ",
  "Last login": "เข้าสู่ระบบล่าสุด",
  "Never": "ยังไม่เคย",
  "Main": "หลัก",
  "Hours": "เวลาทำการ",
  "Online booking": "จองออนไลน์",
  "On": "เปิด",
  "Off": "ปิด",
  "Business Type": "ประเภทธุรกิจ",
  "Discard changes": "ยกเลิกการแก้ไข",
  "Save": "บันทึก",
  "This shop has no subscription record in queza-core.": "ร้านนี้ยังไม่มีข้อมูลแพ็กเกจใน queza-core",
  "Subscription": "แพ็กเกจ",
  "Live from queza-core": "ข้อมูลสดจาก queza-core",
  "Billing cycle": "รอบบิล",
  "Trial ends": "ทดลองใช้ถึง",
  "Next billing": "ครบกำหนดชำระ",
  "Locked price": "ราคาที่ล็อกไว้",
  "mo": "เดือน",
  "yr": "ปี",
  "Current list price": "ราคาปัจจุบันตามแพ็กเกจ",
  "Add-ons": "Add-on",
  "Extend by (days)": "ขยายเวลา (วัน)",
  "New plan": "แพ็กเกจใหม่",
  "Select plan": "เลือกแพ็กเกจ",
  "Confirm": "ยืนยัน",
  "No users.": "ไม่มีผู้ใช้",
  "No branches.": "ไม่มีสาขา",
  "Setup status": "สถานะการตั้งค่า",
  "Customers cannot book via LINE yet": "ลูกค้ายังจองผ่าน LINE ไม่ได้",
  "Users": "ผู้ใช้",
  "Only the owner account so far": "มีแค่บัญชีเจ้าของร้าน",
  "Engagement": "การใช้งานร้าน",
  "Churn risk": "เสี่ยงเลิกใช้",
  "Last booking": "จองล่าสุด",
  "Last login (any user)": "เข้าระบบล่าสุด (ทุกผู้ใช้)",
  "Details": "รายละเอียด",
  "Search...": "ค้นหา...",
  "All": "ทั้งหมด",
  "Payments": "การชำระเงิน",
  "Billing Logs": "ประวัติบิล",
  "Support Tickets": "ตั๋วซัพพอร์ต",
  "Manager": "ผู้จัดการ",
  "Staff": "พนักงาน",
  "User": "ผู้ใช้",
  "2FA": "2FA",
  "AI Center": "ศูนย์ AI",
  "Account": "บัญชี",
  "Account Name": "ชื่อบัญชี",
  "Account Number": "เลขที่บัญชี",
  "Account is Active": "เปิดใช้งานบัญชี",
  "Action": "การกระทำ",
  "Actions": "จัดการ",
  "Active": "ใช้งานอยู่",
  "Active Shops": "ร้านที่ใช้งาน",
  "Actor": "ผู้ทำรายการ",
  "Add Balance Adjustment": "ปรับยอดเงิน",
  "Add Business Type": "เพิ่มประเภทธุรกิจ",
  "Add Override": "เพิ่มการกำหนดเฉพาะร้าน",
  "Add Payment Method": "เพิ่มช่องทางชำระเงิน",
  "Add a business type to get started.": "เพิ่มประเภทธุรกิจเพื่อเริ่มต้น",
  "Add a payment method to get started.": "เพิ่มช่องทางชำระเงินเพื่อเริ่มต้น",
  "Added": "เพิ่มเมื่อ",
  "Address": "ที่อยู่",
  "Adjustment": "ปรับยอด",
  "Adjustment applied.": "ปรับยอดแล้ว",
  "Admin": "ผู้ดูแล",
  "Admin Created": "สร้างผู้ดูแล",
  "Admin Settings": "ตั้งค่าผู้ดูแล",
  "Admin Users": "ผู้ดูแลระบบ",
  "Admin updated.": "แก้ไขผู้ดูแลแล้ว",
  "Admin user created.": "สร้างผู้ดูแลแล้ว",
  "Administration": "การจัดการผู้ดูแล",
  "After": "หลัง",
  "After State": "ข้อมูลหลังแก้ไข",
  "All shop users will see a maintenance page until this is disabled.": "ผู้ใช้ทุกร้านจะเห็นหน้าปิดปรับปรุงจนกว่าจะปิดโหมดนี้",
  "Allow new shops to register on the platform": "อนุญาตให้ร้านใหม่สมัครใช้งาน",
  "Amount": "จำนวนเงิน",
  "Assign to Me": "รับเรื่องเอง",
  "Assigned": "ผู้รับผิดชอบ",
  "Assigned To": "มอบหมายให้",
  "Audit Log Detail": "รายละเอียดประวัติการใช้งาน",
  "Audit Logs": "ประวัติการใช้งาน",
  "Auto-refreshes every 30s · Last:": "รีเฟรชอัตโนมัติทุก 30 วินาที · ล่าสุด:",
  "Back to Shops": "กลับไปหน้าร้านค้า",
  "Balance After": "ยอดหลัง",
  "Balance Before": "ยอดก่อน",
  "Bank Name": "ชื่อธนาคาร",
  "Bank Transfer": "โอนผ่านธนาคาร",
  "Bank transfer ref #": "เลขอ้างอิงการโอน",
  "Before": "ก่อน",
  "Before State": "ข้อมูลก่อนแก้ไข",
  "Billing": "ใบแจ้งหนี้",
  "Body": "ข้อความ",
  "Booking Limit": "จำกัดการจอง",
  "Bookings": "การจอง",
  "Branch": "สาขา",
  "Branch Limit": "จำกัดสาขา",
  "Branches": "สาขา",
  "Business Types": "ประเภทธุรกิจ",
  "Business type created.": "เพิ่มประเภทธุรกิจแล้ว",
  "Business type deleted.": "ลบประเภทธุรกิจแล้ว",
  "Business type updated.": "แก้ไขประเภทธุรกิจแล้ว",
  "CSV exported.": "ส่งออก CSV แล้ว",
  "Cancel": "ยกเลิก",
  "Cancel Subscription": "ยกเลิกแพ็กเกจ",
  "Cancelled": "ยกเลิกแล้ว",
  "Cancelled At": "ยกเลิกเมื่อ",
  "Card Provider / Gateway": "ผู้ให้บริการบัตร / Gateway",
  "Cash": "เงินสด",
  "Change Plan": "เปลี่ยนแพ็กเกจ",
  "Change Plan —": "เปลี่ยนแพ็กเกจ —",
  "Channel": "ช่องทาง",
  "Channels": "ช่องทาง",
  "Checked:": "ตรวจเมื่อ:",
  "Close Ticket": "ปิดเรื่อง",
  "Closed": "ปิดแล้ว",
  "Code": "โค้ด",
  "Communication": "การสื่อสาร",
  "Complete financial transaction ledger": "บัญชีรายการเงินทั้งหมด",
  "Complete trail of all admin actions in the system": "ประวัติการทำงานของผู้ดูแลทั้งหมด",
  "Configuration": "การตั้งค่าระบบ",
  "Configure menu visibility globally, per plan, or per shop": "ตั้งค่าการแสดงเมนู ทั้งระบบ / ตามแพ็กเกจ / รายร้าน",
  "Configure which sidebar menus each role can access": "กำหนดว่าแต่ละบทบาทเห็นเมนูใดบ้าง",
  "Contact & Legal": "ติดต่อและเอกสารกฎหมาย",
  "Control feature availability across plans and individual shops": "เปิด/ปิดฟีเจอร์ตามแพ็กเกจและรายร้าน",
  "Could not create promotion.": "สร้างโปรโมชันไม่สำเร็จ",
  "Create": "สร้าง",
  "Create Admin": "เพิ่มผู้ดูแล",
  "Create Admin User": "เพิ่มผู้ดูแลระบบ",
  "Create Plan": "สร้างแพ็กเกจ",
  "Create Template": "สร้างเทมเพลต",
  "Create promotion": "สร้างโปรโมชัน",
  "Create subscription promotion": "สร้างโปรโมชันแพ็กเกจ",
  "Created": "สร้างเมื่อ",
  "Credit Adjustment": "เพิ่มยอด",
  "Credit Card": "บัตรเครดิต",
  "Currency": "สกุลเงิน",
  "Current plan:": "แพ็กเกจปัจจุบัน:",
  "Customer Limit": "จำกัดลูกค้า",
  "Customers": "ลูกค้า",
  "Customize menu visibility per subscription plan": "ตั้งค่าเมนูตามแพ็กเกจ",
  "Dashboard": "ภาพรวม",
  "Date": "วันที่",
  "Debit Adjustment": "หักยอด",
  "Default Currency": "สกุลเงินหลัก",
  "Default Plan for New Signups": "แพ็กเกจเริ่มต้นสำหรับร้านใหม่",
  "Default visibility for all shops (overridden by plan/shop configs)": "ค่าเริ่มต้นทุกร้าน (แพ็กเกจ/รายร้านตั้งทับได้)",
  "Delete": "ลบ",
  "Delete Business Type": "ลบประเภทธุรกิจ",
  "Delete Payment Method": "ลบช่องทางชำระเงิน",
  "Description": "คำอธิบาย",
  "Detail": "รายละเอียด",
  "Disabled": "ปิด",
  "Discount": "ส่วนลด",
  "Display Name": "ชื่อที่แสดง",
  "Due Date": "ครบกำหนด",
  "Edit": "แก้ไข",
  "Edit Business Type": "แก้ไขประเภทธุรกิจ",
  "Edit Flag —": "แก้ไขฟีเจอร์ —",
  "Edit Payment Method": "แก้ไขช่องทางชำระเงิน",
  "Editing config for shop:": "กำลังแก้ไขของร้าน:",
  "Eligible plans": "แพ็กเกจที่ใช้ได้",
  "Consent Documents": "เอกสารยินยอม (PDPA / ข้อกำหนด)",
  "Terms, privacy (PDPA) and marketing consents shown at sign-up. Editing the text publishes a new version that must be accepted again.": "ข้อกำหนด นโยบายความเป็นส่วนตัว (PDPA) และความยินยอมด้านการตลาดที่แสดงตอนสมัคร · แก้ข้อความ = ออกฉบับใหม่ที่ต้องยอมรับอีกครั้ง",
  "Add document": "เพิ่มเอกสาร",
  "Edit document": "แก้ไขเอกสาร",
  "Shops (queza.online/signup)": "ร้านค้า (หน้าสมัครร้าน)",
  "Affiliates (partner sign-up)": "ตัวแทน (หน้าสมัครตัวแทน)",
  "No documents for this audience — sign-up shows no consent step.": "ยังไม่มีเอกสารของกลุ่มนี้ — หน้าสมัครจะไม่มีขั้นยอมรับ",
  "Required": "บังคับ",
  "Optional": "ไม่บังคับ",
  "Version": "ฉบับที่",
  "Shown": "แสดงอยู่",
  "Accepted this version": "ยอมรับฉบับนี้",
  "all versions": "ทุกฉบับ",
  "Order": "ลำดับ",
  "Hide text": "ซ่อนข้อความ",
  "Show text": "ดูข้อความ",
  "Hide": "ซ่อน",
  "Show": "แสดง",
  "Key (a-z, 0-9, -) — cannot be changed later": "รหัสเอกสาร (a-z, 0-9, -) — แก้ภายหลังไม่ได้",
  "Short summary (shown under the title)": "สรุปสั้น (แสดงใต้หัวข้อ)",
  "Full text": "ข้อความฉบับเต็ม",
  "Required to sign up": "บังคับยอมรับก่อนสมัคร",
  "Publish as a new version — everyone who accepted the previous version of a required document will be asked to accept again. Untick only for a typo fix.": "ออกเป็นฉบับใหม่ — ทุกคนที่เคยยอมรับฉบับก่อนของเอกสารบังคับจะถูกขอให้ยอมรับอีกครั้ง เอาเครื่องหมายออกเฉพาะตอนแก้คำผิด",
  "Approval": "การอนุมัติ",
  "Pending approval": "รออนุมัติ",
  "Approved": "อนุมัติแล้ว",
  "Rejected": "ไม่อนุมัติ",
  "Approve": "อนุมัติ",
  "Reject": "ไม่อนุมัติ",
  "Clicks (30d)": "คลิก (30 วัน)",
  "Payable": "ถอนได้",
  "On hold": "รอครบกำหนด",
  "E-mail (login)": "อีเมล (ใช้เข้าสู่ระบบ)",
  "Mark e-mail verified": "ยืนยันอีเมลให้",
  "Mark phone verified": "ยืนยันเบอร์ให้",
  "verified": "ยืนยันแล้ว",
  "not verified": "ยังไม่ยืนยัน",
  "Bank account": "บัญชีรับเงิน",
  "Portal account": "บัญชีพอร์ทัล",
  "password set": "ตั้งรหัสผ่านแล้ว",
  "no password yet — the person sets it from the portal's \"ตั้ง / ลืมรหัสผ่าน\"": "ยังไม่ตั้งรหัสผ่าน — เจ้าตัวตั้งเองที่พอร์ทัล เมนู \"ตั้ง / ลืมรหัสผ่าน\"",
  "Refund clawback": "หักคืน (คืนเงินร้าน)",
  "Available from": "ถอนได้ตั้งแต่",
  "Select all payable": "เลือกที่ถอนได้ทั้งหมด",
  "Skipped (below minimum payout)": "ข้าม (ยังไม่ถึงขั้นต่ำ)",
  "Bank": "ธนาคาร",
  "Account name": "ชื่อบัญชี",
  "Account number": "เลขบัญชี",
  "Contact verified.": "ยืนยันแล้ว",
  "Approval updated.": "อัปเดตการอนุมัติแล้ว",
  "Sales & Affiliates": "เซลและตัวแทนแนะนำ",
  "Commission is calculated from payments the shops have actually made.": "ค่าคอมมิชชันคิดจากยอดที่ร้านชำระจริงแล้วเท่านั้น",
  "Add referrer": "เพิ่มผู้แนะนำ",
  "Edit referrer": "แก้ไขผู้แนะนำ",
  "Referrers": "ผู้แนะนำ",
  "Commissions": "ค่าคอมมิชชัน",
  "No referrers yet.": "ยังไม่มีผู้แนะนำ",
  "Sales (staff)": "เซล (พนักงาน)",
  "Affiliate": "ตัวแทนอิสระ",
  "Commission": "ค่าคอม",
  "months": "เดือน",
  "Unpaid": "ค้างจ่าย",
  "Referrer": "ผู้แนะนำ",
  "Kind": "ประเภท",
  "Closing bonus": "โบนัสปิดการขาย",
  "of": "ของ",
  "Month": "เดือน",
  "Clear selection": "ล้างที่เลือก",
  "Select all unpaid": "เลือกที่ค้างจ่ายทั้งหมด",
  "Mark as paid": "บันทึกว่าจ่ายแล้ว",
  "Marked as paid": "บันทึกจ่ายแล้ว",
  "No commissions for this filter.": "ไม่มีค่าคอมมิชชันตามตัวกรองนี้",
  "Commission %": "ค่าคอม (%)",
  "For how many months (from the first payment)": "จ่ายกี่เดือน (นับจากการชำระครั้งแรกของร้าน)",
  "Closing bonus — Starter (฿)": "โบนัสปิดการขาย — Starter (บาท)",
  "Closing bonus — Pro (฿)": "โบนัสปิดการขาย — Pro (บาท)",
  "Closing bonus — Business (฿)": "โบนัสปิดการขาย — Business (บาท)",
  "The closing bonus is earned when the shop makes its second payment. An inactive code can no longer be used at sign-up; shops already linked keep earning.": "โบนัสปิดการขายจะได้เมื่อร้านชำระครั้งที่ 2 · รหัสที่ปิดใช้งานจะใช้ตอนสมัครไม่ได้อีก แต่ร้านที่ผูกไว้แล้วยังได้ค่าคอมต่อ",
  "Shop ID (from the shop page)": "รหัสร้าน (Shop ID จากหน้าร้านค้า)",
  "Link shop": "ผูกร้าน",
  "Shop linked.": "ผูกร้านแล้ว",
  "Shop unlinked.": "ยกเลิกการผูกร้านแล้ว",
  "Could not link the shop.": "ผูกร้านไม่สำเร็จ",
  "Could not unlink the shop.": "ยกเลิกการผูกร้านไม่สำเร็จ",
  "Linked": "วันที่ผูก",
  "Added by admin": "ผู้ดูแลเพิ่มให้",
  "Sign-up code": "รหัสตอนสมัคร",
  "Unlink": "ยกเลิกการผูก",
  "No shops linked yet.": "ยังไม่มีร้านที่ผูกไว้",
  "Saved.": "บันทึกแล้ว",
  "Could not save.": "บันทึกไม่สำเร็จ",
  "Cycles": "จำนวนรอบที่ลด",
  "Promotions running now": "โปรโมชันที่ใช้อยู่ตอนนี้",
  "Create a promotion for this plan": "สร้างโปรสำหรับแพ็กเกจนี้",
  "None — shops pay the full price.": "ไม่มี — ร้านจ่ายราคาเต็ม",
  "automatic (no code)": "ใช้อัตโนมัติ (ไม่ต้องพิมพ์โค้ด)",
  "code typed at checkout": "ต้องพิมพ์โค้ดตอนชำระ",
  "Announcements": "ประกาศถึงร้าน",
  "Menu & Button Usage": "สถิติการใช้เมนูและปุ่ม",
  "Business Stats & Targets": "สถิติธุรกิจและเป้ายอด",
  "Accounting Reports": "รายงานบัญชี",
  "First bill only": "บิลแรกเท่านั้น",
  "auto": "อัตโนมัติ",
  "Ends at (optional)": "สิ้นสุดโปร (ไม่บังคับ)",
  "Discounted monthly bills (1 = first bill only)": "ลดกี่บิลรายเดือน (1 = บิลแรกเท่านั้น)",
  "Apply automatically at checkout and show the promo price on the plans page (no code needed)": "ใช้อัตโนมัติตอนชำระเงิน และโชว์ราคาโปรในหน้าแพ็กเกจ (ไม่ต้องพิมพ์โค้ด)",
  "Email subject...": "หัวข้ออีเมล...",
  "Enabled": "เปิด",
  "Enabled For Plans": "เปิดสำหรับแพ็กเกจ",
  "Enter Shop ID (e.g. shop-123)": "กรอกรหัสร้าน (Shop ID)",
  "Error": "ข้อผิดพลาด",
  "Existing subscribers keep their locked price. This price applies to new subscriptions after this time.": "ร้านเดิมใช้ราคาเดิมต่อ ราคานี้ใช้กับการสมัครใหม่หลังเวลานี้",
  "Expired": "หมดอายุ",
  "Export CSV": "ส่งออก CSV",
  "Extend": "ขยายเวลา",
  "Extend Subscription —": "ขยายเวลาแพ็กเกจ —",
  "Extend by": "ขยาย",
  "Failed": "ไม่สำเร็จ",
  "Failed Payment": "ชำระไม่สำเร็จ",
  "Failed Payments": "ชำระเงินไม่สำเร็จ",
  "Failed to add override.": "เพิ่มไม่สำเร็จ",
  "Failed to apply adjustment.": "ปรับยอดไม่สำเร็จ",
  "Failed to create admin.": "เพิ่มผู้ดูแลไม่สำเร็จ",
  "Failed to load admin users.": "โหลดรายชื่อผู้ดูแลไม่ได้",
  "Failed to load audit logs.": "โหลดประวัติไม่ได้",
  "Failed to load business types": "โหลดประเภทธุรกิจไม่ได้",
  "Failed to load dashboard.": "โหลดภาพรวมไม่ได้",
  "Failed to load feature flags.": "โหลดฟีเจอร์ไม่ได้",
  "Failed to load money logs.": "โหลดรายการเงินไม่ได้",
  "Failed to load payment methods": "โหลดช่องทางชำระเงินไม่ได้",
  "Failed to load settings.": "โหลดการตั้งค่าไม่ได้",
  "Failed to load shop.": "โหลดข้อมูลร้านไม่ได้",
  "Failed to load shops.": "โหลดรายชื่อร้านไม่ได้",
  "Failed to load subscriptions.": "โหลดแพ็กเกจร้านไม่ได้",
  "Failed to reactivate shop.": "เปิดใช้งานร้านไม่สำเร็จ",
  "Failed to record payment.": "บันทึกการชำระไม่สำเร็จ",
  "Failed to save permissions.": "บันทึกสิทธิ์ไม่สำเร็จ",
  "Failed to save plan.": "บันทึกแพ็กเกจไม่สำเร็จ",
  "Failed to save template.": "บันทึกเทมเพลตไม่สำเร็จ",
  "Failed to save.": "บันทึกไม่สำเร็จ",
  "Failed to send reply.": "ส่งคำตอบไม่สำเร็จ",
  "Failed to suspend shop.": "ระงับร้านไม่สำเร็จ",
  "Failed to update flag.": "แก้ไขฟีเจอร์ไม่สำเร็จ",
  "Failed to update ticket.": "แก้ไขเรื่องไม่สำเร็จ",
  "Failed to update.": "แก้ไขไม่สำเร็จ",
  "Feature Flags": "เปิด/ปิดฟีเจอร์",
  "Feature flag updated.": "แก้ไขฟีเจอร์แล้ว",
  "Features": "ฟีเจอร์",
  "Finance": "การเงิน",
  "First Name": "ชื่อ",
  "First verified subscription payment only": "เฉพาะการชำระครั้งแรกที่ตรวจแล้ว",
  "Fixed (satang)": "จำนวนเงิน (สตางค์)",
  "Flag Updated": "แก้ไขฟีเจอร์",
  "Flag settings saved.": "บันทึกฟีเจอร์แล้ว",
  "General": "ทั่วไป",
  "Global Config": "ทั้งระบบ",
  "Global Menu Configuration": "ตั้งค่าเมนูทั้งระบบ",
  "Global config saved.": "บันทึกแล้ว",
  "Hidden": "ซ่อน",
  "High": "สูง",
  "Inactive": "ปิดใช้งาน",
  "Instructions": "คำแนะนำการชำระ",
  "Internal Note": "บันทึกภายใน",
  "Internal notes...": "บันทึกภายใน...",
  "Invoice #": "เลขที่ใบแจ้งหนี้",
  "Invoice Paid": "ชำระใบแจ้งหนี้",
  "Invoices, payments, and financial adjustments": "ใบแจ้งหนี้ การชำระ และการปรับยอด",
  "Item": "รายการ",
  "Label": "ชื่อ",
  "Last Login": "เข้าใช้ล่าสุด",
  "Last Name": "นามสกุล",
  "Last updated": "แก้ไขล่าสุด",
  "Last updated:": "อัปเดตล่าสุด:",
  "Latency": "เวลาตอบสนอง",
  "Load Config": "โหลดการตั้งค่า",
  "Loading QR…": "กำลังโหลด QR…",
  "Loading Queza Admin...": "กำลังโหลด...",
  "Loading SMTP settings…": "กำลังโหลด...",
  "Loading dashboard...": "กำลังโหลด...",
  "Loading feature flags...": "กำลังโหลด...",
  "Loading plans...": "กำลังโหลด...",
  "Loading settings...": "กำลังโหลด...",
  "Loading shop...": "กำลังโหลด...",
  "Loading…": "กำลังโหลด…",
  "Locked (Upgrade)": "ล็อก (ต้องอัปเกรด)",
  "Logout": "ออกจากระบบ",
  "Low": "ต่ำ",
  "Maintenance": "ปิดปรับปรุง",
  "Maintenance Mode": "โหมดปิดปรับปรุง",
  "Maintenance Mode is ACTIVE": "เปิดโหมดปิดปรับปรุงอยู่",
  "Manage all registered beauty & wellness shops": "ร้านค้าทั้งหมดในระบบ",
  "Manage internal admin accounts and roles": "จัดการบัญชีและบทบาทของผู้ดูแล",
  "Manage shop subscription plans and billing cycles": "จัดการแพ็กเกจและรอบบิลของร้าน",
  "Manage subscription plans and features": "จัดการแพ็กเกจและฟีเจอร์",
  "Manage support tickets and customer communications": "จัดการเรื่องแจ้งปัญหาจากร้าน",
  "Managed in queza-core — view only here": "จัดการใน queza-core — ดูได้อย่างเดียว",
  "Manual Charge": "ชำระด้วยมือ",
  "Manual Payment": "บันทึกการชำระเอง",
  "Manual payment recorded.": "บันทึกการชำระแล้ว",
  "Manual top-up": "เติมเงินเอง",
  "Master Data": "ข้อมูลหลัก",
  "Medium": "กลาง",
  "Memberships": "สมาชิก",
  "Menu Control": "ควบคุมเมนู",
  "Menu Item": "เมนู",
  "Messages (": "ข้อความ (",
  "Metadata": "ข้อมูลเพิ่มเติม",
  "Money Logs": "รายการเงิน",
  "Monthly": "รายเดือน",
  "Monthly Revenue": "รายได้ต่อเดือน",
  "Multi-Branch": "หลายสาขา",
  "My Account": "บัญชีของฉัน",
  "My Account & 2FA": "บัญชีของฉัน & 2FA",
  "My account": "บัญชีของฉัน",
  "Name": "ชื่อ",
  "Name (snake_case)": "ชื่อ (snake_case)",
  "Name is required.": "กรุณากรอกชื่อ",
  "Needs attention": "ต้องตรวจสอบ",
  "New Plan": "แพ็กเกจใหม่",
  "New Signups Enabled": "เปิดรับสมัครร้านใหม่",
  "New price effective from": "ราคาใหม่มีผลตั้งแต่",
  "Next Billing": "รอบบิลถัดไป",
  "Next price:": "ราคาถัดไป:",
  "No adjustments found.": "ไม่มีรายการปรับยอด",
  "No admin users found.": "ไม่มีผู้ดูแล",
  "No audit logs found.": "ไม่มีประวัติ",
  "No billing records found.": "ไม่มีรายการ",
  "No business types found.": "ไม่มีประเภทธุรกิจ",
  "No business types yet": "ยังไม่มีประเภทธุรกิจ",
  "No invoices found.": "ไม่มีใบแจ้งหนี้",
  "No logs found.": "ไม่มีประวัติ",
  "No money logs found.": "ไม่มีรายการเงิน",
  "No notifications sent to this shop.": "ยังไม่เคยส่งแจ้งเตือนถึงร้านนี้",
  "No overrides configured.": "ยังไม่มีการกำหนดเฉพาะร้าน",
  "No payment methods found.": "ไม่มีช่องทางชำระเงิน",
  "No payment methods yet": "ยังไม่มีช่องทางชำระเงิน",
  "No promotions.": "ยังไม่มีโปรโมชัน",
  "No recent activity.": "ยังไม่มีความเคลื่อนไหว",
  "No recent errors. All clear!": "ไม่มีข้อผิดพลาดล่าสุด",
  "No shop": "ไม่มีร้าน",
  "No shops found.": "ไม่พบร้านค้า",
  "No subject": "ไม่มีหัวข้อ",
  "No subscriptions found.": "ไม่พบแพ็กเกจร้าน",
  "No support tickets.": "ไม่มีเรื่องแจ้งปัญหา",
  "No templates found.": "ไม่มีเทมเพลต",
  "No tickets found.": "ไม่พบเรื่องแจ้งปัญหา",
  "No timeline events.": "ยังไม่มีประวัติ",
  "None": "ไม่มี",
  "Notification Automation": "แจ้งเตือนอัตโนมัติ",
  "Notification message...": "ข้อความแจ้งเตือน...",
  "Notification title...": "หัวข้อแจ้งเตือน...",
  "Notifications": "แจ้งเตือน",
  "Number of days": "จำนวนวัน",
  "Open": "เปิด",
  "Open Tickets": "เรื่องที่ยังเปิดอยู่",
  "Operations": "งานร้านค้า",
  "Optional description...": "คำอธิบาย (ถ้ามี)...",
  "Optional instructions shown to the payer...": "คำแนะนำสำหรับผู้ชำระ (ถ้ามี)...",
  "Original Message": "ข้อความเดิม",
  "Overdue": "เกินกำหนด",
  "Override": "กำหนดเฉพาะร้าน",
  "Override added.": "เพิ่มแล้ว",
  "Override menu config for a specific shop": "ตั้งค่าเมนูเฉพาะร้าน",
  "Override removed.": "ลบแล้ว",
  "Overview": "ภาพรวม",
  "Owner": "เจ้าของร้าน",
  "Owner Information": "ข้อมูลเจ้าของร้าน",
  "POS Bills": "บิล POS",
  "POS System": "ระบบ POS",
  "Paid": "ชำระแล้ว",
  "Paid At": "ชำระเมื่อ",
  "Password": "รหัสผ่าน",
  "Past Due": "ค้างชำระ",
  "Payment Method": "ช่องทางชำระเงิน",
  "Payment Methods": "ช่องทางชำระเงิน",
  "Payment method created.": "เพิ่มช่องทางชำระเงินแล้ว",
  "Payment method deleted.": "ลบช่องทางชำระเงินแล้ว",
  "Payment method updated.": "แก้ไขช่องทางชำระเงินแล้ว",
  "Pending": "รอดำเนินการ",
  "Percent": "เปอร์เซ็นต์",
  "Period End": "สิ้นสุดรอบ",
  "Period Start": "เริ่มรอบ",
  "Phone": "โทรศัพท์",
  "Plan": "แพ็กเกจ",
  "Plan Changed": "เปลี่ยนแพ็กเกจ",
  "Plan Configs": "ตามแพ็กเกจ",
  "Plan Menu Configuration": "ตั้งค่าเมนูตามแพ็กเกจ",
  "Plan Promotions": "โปรโมชันแพ็กเกจ",
  "Plan changed successfully.": "เปลี่ยนแพ็กเกจแล้ว",
  "Plan is Active": "เปิดขายแพ็กเกจ",
  "Plans": "แพ็กเกจ",
  "Please provide a reason...": "กรุณาระบุเหตุผล...",
  "Please select an image file.": "กรุณาเลือกไฟล์รูป",
  "Please try again.": "กรุณาลองใหม่",
  "Priority": "ความสำคัญ",
  "Privacy Policy URL": "ลิงก์นโยบายความเป็นส่วนตัว",
  "Promotion": "โปรโมชัน",
  "Promotion created.": "สร้างโปรโมชันแล้ว",
  "QR Code (optional)": "QR Code (ถ้ามี)",
  "QR image too large (max ~500KB).": "รูป QR ใหญ่เกินไป (สูงสุด ~500KB)",
  "Quota (optional)": "จำนวนสิทธิ์ (ถ้ามี)",
  "Reactivate": "เปิดใช้งานอีกครั้ง",
  "Real-time status of all platform services": "สถานะบริการทั้งหมดแบบเรียลไทม์",
  "Reason": "เหตุผล",
  "Reason for adjustment...": "เหตุผลที่ปรับยอด...",
  "Reason for extension...": "เหตุผลที่ขยายเวลา...",
  "Reason for plan change...": "เหตุผลที่เปลี่ยนแพ็กเกจ...",
  "Reason...": "เหตุผล...",
  "Recent Activity": "ความเคลื่อนไหวล่าสุด",
  "Recent Errors & Warnings": "ข้อผิดพลาดล่าสุด",
  "Recipients": "ผู้รับ",
  "Record Manual Payment": "บันทึกการชำระเอง",
  "Reference": "อ้างอิง",
  "Refresh": "รีเฟรช",
  "Refund": "คืนเงิน",
  "Refunded": "คืนเงินแล้ว",
  "Remove": "ลบ",
  "Reply": "ตอบกลับ",
  "Reply sent.": "ส่งคำตอบแล้ว",
  "Reports & Analytics": "รายงาน",
  "Reset": "รีเซ็ต",
  "Resolved": "แก้ไขแล้ว",
  "Retry": "ลองใหม่",
  "Role": "บทบาท",
  "Role Permissions": "สิทธิ์ตามบทบาท",
  "Role permissions saved.": "บันทึกสิทธิ์แล้ว",
  "Rollout Percentage": "เปิดให้ (%)",
  "SMTP settings saved — queza-core will use them within 30 seconds.": "บันทึก SMTP แล้ว — ระบบจะใช้ภายใน 30 วินาที",
  "Search by admin name...": "ค้นหาชื่อผู้ดูแล...",
  "Search by name, email, phone...": "ค้นหาชื่อ อีเมล เบอร์โทร...",
  "Search by shop name...": "ค้นหาชื่อร้าน...",
  "Search by shop...": "ค้นหาร้าน...",
  "Search by subject, shop...": "ค้นหาหัวข้อ ร้าน...",
  "Security": "ความปลอดภัย",
  "Settings for the whole platform · General / Contact / Plans are saved but not yet used by queza-core (no effect on shops)": "ตั้งค่าทั้งระบบ · ส่วนทั่วไป / ติดต่อ / แพ็กเกจ บันทึกได้แต่ queza-core ยังไม่ได้ใช้ค่า (ยังไม่มีผลกับร้าน)",
  "Select plan...": "เลือกแพ็กเกจ...",
  "Send New Notification": "ส่งแจ้งเตือนใหม่",
  "Send notifications, manage templates, and view delivery logs": "ส่งแจ้งเตือน จัดการเทมเพลต และดูประวัติการส่ง",
  "Sent": "ส่งแล้ว",
  "Sent At": "ส่งเมื่อ",
  "Settings saved successfully.": "บันทึกการตั้งค่าแล้ว",
  "Shop": "ร้าน",
  "Shop ID": "รหัสร้าน",
  "Shop Information": "ข้อมูลร้าน",
  "Shop Name": "ชื่อร้าน",
  "Shop Overrides": "รายร้าน",
  "Shop Payments": "การชำระเงินของร้าน",
  "Shop Reactivated": "เปิดร้านอีกครั้ง",
  "Shop Suspended": "ระงับร้าน",
  "Shop override saved.": "บันทึกแล้ว",
  "Shop reactivated successfully.": "เปิดใช้งานร้านแล้ว",
  "Shop reactivated.": "เปิดใช้งานร้านแล้ว",
  "Shop suspended successfully.": "ระงับร้านแล้ว",
  "Shop suspended.": "ระงับร้านแล้ว",
  "Shop-Level Menu Overrides": "ตั้งค่าเมนูรายร้าน",
  "Shops": "ร้านค้า",
  "Shops by Status": "ร้านตามสถานะ",
  "Showing": "แสดง",
  "Sign-in security (2FA)": "ความปลอดภัยการเข้าสู่ระบบ (2FA)",
  "Slip": "สลิป",
  "Sort Order": "ลำดับ",
  "Source": "แหล่งที่มา",
  "Staff Limit": "จำกัดพนักงาน",
  "Staff Members": "พนักงาน",
  "Status": "สถานะ",
  "Status updated.": "อัปเดตสถานะแล้ว",
  "Subject": "หัวข้อ",
  "Subscription Created": "สร้างแพ็กเกจ",
  "Subscription Payment": "ชำระค่าแพ็กเกจ",
  "Subscription cancelled.": "ยกเลิกแพ็กเกจแล้ว",
  "Subscription extended.": "ขยายเวลาแล้ว",
  "Subscription reactivated.": "เปิดแพ็กเกจอีกครั้งแล้ว",
  "Subscriptions": "แพ็กเกจของร้าน",
  "Super Admin": "ผู้ดูแลสูงสุด",
  "Support": "แจ้งปัญหา",
  "Support Console": "คอนโซลดูแลระบบ",
  "Support Email": "อีเมลซัพพอร์ต",
  "Support tickets": "เรื่องแจ้งปัญหา",
  "Suspend": "ระงับ",
  "Suspend Shop": "ระงับร้าน",
  "Suspended": "ถูกระงับ",
  "Suspended Shops": "ร้านที่ถูกระงับ",
  "System": "ระบบ",
  "System Health": "สถานะระบบ",
  "AI Usage": "การใช้งาน AI",
  "Shops map": "แผนที่ร้าน",
  "Customer Analytics": "วิเคราะห์ลูกค้า",
  "End users of every shop on Queza: where they come from, who they are, and how many booking-page visits turn into bookings. Aggregated only — no names or phone numbers.": "ลูกค้าปลายทางของทุกร้านบน Queza: มาจากช่องทางไหน เป็นใคร และเปิดหน้าจองแล้วจองจริงกี่ % — ข้อมูลรวมเท่านั้น ไม่มีชื่อ/เบอร์",
  "Booking page visits": "เปิดหน้าจอง",
  "Online bookings": "จองออนไลน์",
  "Visit → booking": "เปิดหน้า → จอง",
  "Customers (all time)": "ลูกค้าสะสม",
  "With profile": "กรอกโปรไฟล์",
  "gender / age given": "มีเพศ/อายุ",
  "Marketing consent": "ยินยอมรับข่าวสาร",
  "people": "คน",
  "LINE connected": "เชื่อม LINE",
  "Shops with visits": "ร้านที่มีคนเปิดหน้าจอง",
  "Channel → visits → bookings": "ช่องทาง → เปิดหน้าจอง → จอง",
  "Channel": "ช่องทาง",
  "Visits": "เปิดหน้าจอง",
  "Converted visits": "เปิดแล้วจอง",
  "Conversion": "จองจริง %",
  "Gender": "เพศ",
  "Age": "ช่วงอายุ",
  "Device": "อุปกรณ์",
  "First channel (acquisition)": "ช่องทางแรกที่รู้จักร้าน",
  "Top districts": "เขต/อำเภอที่ลูกค้าอยู่",
  "Interests": "ความสนใจ",
  "Campaigns (utm_campaign) → bookings": "แคมเปญ (utm_campaign) → จอง",
  "Campaign": "แคมเปญ",
  "Per shop": "รายร้าน",
  "Consent": "ยินยอม",
  "this month": "เดือนนี้",
  "No data": "ยังไม่มีข้อมูล",
  "Where every shop on Queza is — pins come from the branch coordinates shops set in their backoffice": "ร้านบน Queza อยู่จุดไหนบ้าง — หมุดมาจากพิกัดสาขาที่ร้านปักไว้ในหลังร้าน",
  "Search shop...": "ค้นหาร้าน...",
  "All plans": "ทุกแพ็กเกจ",
  "Pinned": "ปักหมุดแล้ว",
  "Not pinned": "ยังไม่ปักหมุด",
  "Branches on map": "สาขาบนแผนที่",
  "Suspended / inactive": "ระงับ / ไม่ใช้งาน",
  "Shops without a pin": "ร้านที่ยังไม่ปักหมุด",
  "The shop pins its branches itself (Backoffice → Branches → Edit → map). Ask them, or set it for them from the shop page.": "ร้านปักหมุดสาขาเองที่ หลังร้าน → สาขา → แก้ไข → แผนที่ — แจ้งร้าน หรือดูรายละเอียดร้านจากหน้าร้านค้า",
  "Activate (skip trial)": "เปิดใช้งานจริง (ข้าม trial)",
  "The shop becomes a paying (active) subscription on its current plan without a payment record. Use for test shops or manual sales.": "ร้านจะเปลี่ยนจากทดลองใช้เป็นใช้งานจริง (active) บนแพ็กเกจเดิม โดยไม่มีบันทึกการชำระเงิน ใช้กับร้านทดสอบหรือขายนอกระบบ",
  "Manage": "จัดการ",
  "Grant AI add-on": "เปิด AI add-on",
  "Duration": "ระยะเวลา",
  "No expiry (until removed)": "ไม่กำหนดวันหมด (จนกว่าจะปิดเอง)",
  "month(s) — then removed automatically": "เดือน — ครบแล้วระบบปิดให้อัตโนมัติ",
  "Current expiry:": "หมดอายุปัจจุบัน:",
  "until": "ถึง",
  "AI Add-ons": "AI Add-on",
  "AI Add-ons —": "AI Add-on —",
  "Grants the add-on directly (no payment record). Normal sales go through the shop paying in backoffice → Shop Payments approve.": "เปิดให้ทันทีโดยไม่มีบันทึกการชำระเงิน (ใช้สำหรับทดลองใช้/ขายนอกระบบ) การขายปกติให้ร้านชำระในหลังร้านแล้วอนุมัติที่ Shop Payments",
  "Daily brief, insights, staff coach, revenue growth": "สรุปรายวัน, insight, โค้ชพนักงาน, แผนเพิ่มยอด",
  "+ customer segments & win-back": "+ แบ่งกลุ่มลูกค้าและดึงลูกค้าเก่า",
  "+ fraud / loss detection": "+ ตรวจจับทุจริต/รั่วไหล",
  "e.g. free trial 1 month / paid by bank transfer": "เช่น ทดลองใช้ฟรี 1 เดือน / ชำระผ่านโอนเงินแล้ว",
  "Save Add-ons": "บันทึก Add-on",
  "What each shop's AI analysis costs us (Claude API), per month": "ค่าใช้จ่าย AI (Claude API) ที่แต่ละร้านใช้วิเคราะห์ รายเดือน",
  "Total cost": "ค่าใช้จ่ายรวม",
  "Shops using AI": "ร้านที่ใช้ AI",
  "Reports": "รายงาน",
  "nightly + manual": "ทุกคืน + กดเอง",
  "Questions": "คำถาม",
  "Tokens": "โทเค็น",
  "out": "ออก",
  "Failed AI calls this month": "เรียก AI ไม่สำเร็จเดือนนี้",
  "Daily cost": "ค่าใช้จ่ายรายวัน",
  "calls": "ครั้ง",
  "Cost": "ค่าใช้จ่าย",
  "Last call": "ใช้ล่าสุด",
  "No AI usage this month": "เดือนนี้ยังไม่มีการใช้ AI",
  "Open shop": "เปิดหน้าร้าน",
  "failed": "ไม่สำเร็จ",
  "Model": "โมเดล",
  "Note": "หมายเหตุ",
  "Nightly report": "รายงานรายคืน",
  "Manual report": "รายงานกดเอง",
  "Question": "คำถาม",
  "System Settings": "ตั้งค่าระบบ",
  "Integrations": "การเชื่อมต่อระบบ",
  "THB — Thai Baht": "THB — บาท",
  "Tags": "แท็ก",
  "Target": "เป้าหมาย",
  "Target ID": "รหัสเป้าหมาย",
  "Target Type": "ประเภทเป้าหมาย",
  "Template is Active": "เปิดใช้เทมเพลต",
  "Template saved.": "บันทึกเทมเพลตแล้ว",
  "Terms of Service URL": "ลิงก์ข้อตกลงการใช้งาน",
  "The shop will lose access at the end of their current billing period.": "ร้านจะใช้งานไม่ได้เมื่อสิ้นสุดรอบบิลปัจจุบัน",
  "This will block the shop owner's access immediately.": "เจ้าของร้านจะเข้าใช้งานไม่ได้ทันที",
  "This will prevent the shop owner from accessing the platform. You must provide a reason.": "เจ้าของร้านจะเข้าใช้งานไม่ได้ ต้องระบุเหตุผล",
  "This will restore the shop's access to the platform.": "ร้านจะกลับมาใช้งานได้",
  "This will restore the shop's platform access.": "ร้านจะกลับมาใช้งานได้",
  "This will restore the subscription and renew billing.": "แพ็กเกจจะกลับมาใช้งานและเริ่มรอบบิลใหม่",
  "Ticket #": "เลขที่เรื่อง",
  "Ticket assigned to you.": "รับเรื่องแล้ว",
  "Ticket closed.": "ปิดเรื่องแล้ว",
  "Ticket updated.": "อัปเดตเรื่องแล้ว",
  "recorded": "บันทึกไว้",
  "Timeline": "ไทม์ไลน์",
  "Timezone Override": "กำหนดเขตเวลา",
  "Title": "หัวข้อ",
  "Today Revenue": "รายได้วันนี้",
  "Total Paid": "ชำระแล้วทั้งหมด",
  "Total Shops": "ร้านทั้งหมด",
  "Total Unpaid": "ค้างชำระทั้งหมด",
  "Transaction Detail": "รายละเอียดรายการ",
  "Trial": "ทดลองใช้",
  "Trial Days": "จำนวนวันทดลองใช้",
  "Trial End": "สิ้นสุดทดลอง",
  "Trial Ends": "สิ้นสุดทดลอง",
  "Trial Shops": "ร้านทดลองใช้",
  "Trial Start": "เริ่มทดลอง",
  "Trial Started": "เริ่มทดลองใช้",
  "Try Again": "ลองใหม่",
  "Type": "ประเภท",
  "USD — US Dollar": "USD — ดอลลาร์สหรัฐ",
  "Unassigned": "ยังไม่มีผู้รับ",
  "Urgent": "ด่วน",
  "Usage": "การใช้งาน",
  "VAT Rate (%)": "อัตรา VAT (%)",
  "View Details": "ดูรายละเอียด",
  "Visible": "แสดง",
  "Wallet Credit": "เติม Wallet",
  "Wallet Debit": "หัก Wallet",
  "When enabled, all shops see a maintenance page": "เมื่อเปิด ทุกร้านจะเห็นหน้าปิดปรับปรุง",
  "Yearly": "รายปี",
  "bookings/mo": "การจอง/เดือน",
  "e.g. Hair Salon": "เช่น ร้านทำผม",
  "phone number / national ID": "เบอร์โทร / เลขบัตรประชาชน",
  "Sign In": "เข้าสู่ระบบ",
  "Signing in...": "กำลังเข้าสู่ระบบ...",
  "Verify": "ยืนยัน",
  "Verifying...": "กำลังตรวจสอบ...",
  "Turn on & sign in": "เปิดใช้และเข้าสู่ระบบ",
  "Save Settings": "บันทึกการตั้งค่า",
  "Saving...": "กำลังบันทึก...",
  "Save SMTP": "บันทึก SMTP",
  "Send test": "ส่งทดสอบ",
  "Sending…": "กำลังส่ง…",
  "Saving…": "กำลังบันทึก…",
  "Send Now": "ส่งเลย",
  "Sending...": "กำลังส่ง...",
  "All Shops": "ทุกร้าน",
  "Specific Shops": "เลือกร้าน",
  "Templates": "เทมเพลต",
  "Logs": "ประวัติ",
  // roles & permissions / audit log
  "Role saved.": "บันทึกบทบาทแล้ว",
  "Failed to save role.": "บันทึกบทบาทไม่สำเร็จ",
  "Role reset to default permissions.": "คืนค่าสิทธิ์เริ่มต้นของบทบาทแล้ว",
  "Role created.": "สร้างบทบาทแล้ว",
  "Failed to create role.": "สร้างบทบาทไม่สำเร็จ",
  "Role deleted.": "ลบบทบาทแล้ว",
  "Failed to delete role.": "ลบบทบาทไม่สำเร็จ",
  "Roles & Permissions": "สิทธิ์และบทบาท",
  "Choose what each role can see and do on every menu. Per-person adjustments are on the Admin Users page.": "กำหนดว่าแต่ละบทบาทเห็นและทำอะไรได้บ้างในทุกเมนู — ปรับสิทธิ์เฉพาะคนได้ที่หน้าผู้ดูแลระบบ",
  "Change history": "ประวัติการแก้สิทธิ์",
  "Create role": "สร้างบทบาท",
  "Discard unsaved changes?": "ทิ้งการแก้ไขที่ยังไม่บันทึก?",
  "All permissions": "ทุกสิทธิ์",
  "Built-in": "บทบาทหลัก",
  "Custom": "สร้างเอง",
  "permissions": "สิทธิ์",
  "Role name": "ชื่อบทบาท",
  "Super Admin always has every permission and cannot be changed.": "ผู้ดูแลสูงสุดมีทุกสิทธิ์เสมอ แก้ไขไม่ได้",
  "This is your own role — only a Super Admin can change it.": "นี่คือบทบาทของคุณเอง — ต้องให้ผู้ดูแลสูงสุดเป็นคนแก้",
  "Using the built-in default permissions.": "ใช้สิทธิ์ค่าเริ่มต้นของระบบอยู่",
  "Admins with this role": "ผู้ดูแลที่ใช้บทบาทนี้",
  "added": "เพิ่ม",
  "removed": "เอาออก",
  "affects": "มีผลกับ",
  "admins": "คน",
  "No changes": "ยังไม่มีการแก้ไข",
  "Delete role": "ลบบทบาท",
  "Reset to default": "คืนค่าเริ่มต้น",
  "e.g. Accounting team": "เช่น ทีมบัญชี",
  "Role code (a-z, 0-9, _)": "รหัสบทบาท (a-z, 0-9, _)",
  "Start with a letter; use a-z, 0-9 and _ only (2–40).": "ขึ้นต้นด้วยตัวอักษร ใช้ได้เฉพาะ a-z 0-9 และ _ (2–40 ตัว)",
  "Start from": "เริ่มจาก",
  "No permissions (empty)": "ไม่มีสิทธิ์ (ว่าง)",
  "Copy from": "คัดลอกจาก",
  "The role will be removed. Admins must be moved to another role first.": "บทบาทนี้จะถูกลบ — ต้องย้ายผู้ดูแลไปบทบาทอื่นก่อน",
  "Personal permissions saved.": "บันทึกสิทธิ์รายคนแล้ว",
  "Personal permissions": "สิทธิ์รายคน",
  "Tick to add a permission for this person only, untick to take one away.": "ติ๊กเพื่อเพิ่มสิทธิ์ให้คนนี้คนเดียว เอาติ๊กออกเพื่อตัดสิทธิ์",
  "You cannot change your own permissions.": "แก้สิทธิ์ของตัวเองไม่ได้",
  "added for this person": "เพิ่มให้คนนี้",
  "removed for this person": "ตัดออกสำหรับคนนี้",
  "Same as the role": "เหมือนบทบาท (ไม่มีปรับรายคน)",
  "Back to role permissions": "กลับไปใช้สิทธิ์ตามบทบาท",
  "Has personal permission adjustments": "มีการปรับสิทธิ์เฉพาะคน",
  "Adjusted": "ปรับรายคน",
  "Deactivate": "ปิดใช้งาน",
  "Activate": "เปิดใช้งาน",
  "Select all": "เลือกทั้งหมด",
  "Clear all": "ล้างทั้งหมด",
  "Added for this person": "เพิ่มให้คนนี้",
  "Removed for this person": "ตัดออกสำหรับคนนี้",
  "Menu": "เมนู",
  "Allowed actions": "สิ่งที่ทำได้",
  "Red actions change real data or money — grant them carefully. Every change is recorded in the audit log.": "สิทธิ์สีแดงเปลี่ยนข้อมูลจริงหรือเงิน — ให้อย่างระวัง ทุกการแก้สิทธิ์ถูกบันทึกในประวัติการใช้งาน",
  "Roles & permissions (all)": "สิทธิ์และบทบาท (ทั้งหมด)",
  "Personal permissions changed": "ปรับสิทธิ์รายคน",
  "Permission denied": "ถูกปฏิเสธ (ไม่มีสิทธิ์)",
  "Failed sign-in": "เข้าสู่ระบบไม่สำเร็จ",
  "Admin accounts (all)": "บัญชีผู้ดูแล (ทั้งหมด)",
  "Shops (all)": "ร้านค้า (ทั้งหมด)",
  "Shop subscriptions (all)": "แพ็กเกจของร้าน (ทั้งหมด)",
  "Billing & money (all)": "การเงิน (ทั้งหมด)",
  "Plans (all)": "แพ็กเกจ (ทั้งหมด)",
  "Plan promotions (all)": "โปรโมชันแพ็กเกจ (ทั้งหมด)",
  "Feature flags (all)": "เปิด/ปิดฟีเจอร์ (ทั้งหมด)",
  "Menu control (all)": "ควบคุมเมนู (ทั้งหมด)",
  "Business types (all)": "ประเภทธุรกิจ (ทั้งหมด)",
  "Payment methods (all)": "ช่องทางชำระเงิน (ทั้งหมด)",
  "Notifications (all)": "แจ้งเตือน (ทั้งหมด)",
  "Support tickets (all)": "แจ้งปัญหา (ทั้งหมด)",
  "System settings (all)": "ตั้งค่าระบบ (ทั้งหมด)",
  "No before/after data for this action.": "รายการนี้ไม่มีข้อมูลก่อน/หลัง",
  "No field changed.": "ไม่มีช่องที่เปลี่ยน",
  "Field": "ช่อง",
  "Same items, different order": "รายการเดิม แค่ลำดับต่างกัน",
  "Changed": "ที่เปลี่ยน",
  "Email / role": "อีเมล / บทบาท",
  "Request / device": "คำขอ / อุปกรณ์",
  "What changed": "สิ่งที่เปลี่ยน",
  "No access to this page": "ไม่มีสิทธิ์เข้าหน้านี้",
  "Ask an admin who manages permissions to grant it.": "ขอให้ผู้ดูแลที่จัดการสิทธิ์เปิดสิทธิ์ให้",
  "Back to dashboard": "กลับหน้าภาพรวม",
}

/** Translate an English UI string (returns it unchanged in English mode or when there's no translation). */
export function tx(s: string): string {
  return lang === 'th' ? TH[s] ?? s : s
}

if (typeof document !== 'undefined') document.documentElement.lang = lang
